import type { AdminUserView } from "@webuild-ai/admin/models/components";

import React, { useMemo, useState } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Input,
  Select,
  SelectItem,
  User as UserAvatar,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  addToast,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { useAdminStore } from "@/stores/AdminStore";
import { logger } from "@/telemetry";

type Role = "Admin" | "Manager" | "User";

interface UsersTabProps {
  users: AdminUserView[];
  onUsersChanged: () => Promise<void>;
}

export const UsersTab: React.FC<UsersTabProps> = ({ users, onUsersChanged }) => {
  const { t } = useTranslation();
  const { adminClient } = useWebuildAPIClient();
  const { changeUserRole, bulkChangeUserRoles } = useAdminStore();

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<"All" | Role>("All");
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [confirmModal, setConfirmModal] = useState<{
    userId: string;
    userName: string;
    currentRole: string;
    newRole: Role;
  } | null>(null);
  const [bulkModal, setBulkModal] = useState<{ newRole: Role } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const matchesSearch =
        !search ||
        u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase());
      const matchesRole = roleFilter === "All" || u.role === roleFilter;

      return matchesSearch && matchesRole;
    });
  }, [users, search, roleFilter]);

  const selectedCount = selectedKeys.size;

  const handleRoleChange = (user: AdminUserView, newRole: Role) => {
    if (newRole === user.role) return;
    setConfirmModal({
      userId: user.id,
      userName: user.name,
      currentRole: user.role,
      newRole,
    });
  };

  const confirmRoleChange = async () => {
    if (!confirmModal || !adminClient) return;
    setIsSubmitting(true);
    try {
      await changeUserRole(adminClient, confirmModal.userId, confirmModal.newRole);
      addToast({
        title: t("Role updated"),
        description: t("{{name}}'s role changed to {{role}}", {
          name: confirmModal.userName,
          role: confirmModal.newRole,
        }),
        color: "success",
      });
      await onUsersChanged();
    } catch (error) {
      logger.error("Failed to change user role", error instanceof Error ? error : undefined);
      addToast({
        title: t("Error"),
        description:
          error instanceof Error ? error.message : t("Failed to update role. Please try again."),
        color: "danger",
      });
    } finally {
      setIsSubmitting(false);
      setConfirmModal(null);
    }
  };

  const confirmBulkRoleChange = async () => {
    if (!bulkModal || !adminClient || selectedCount === 0) return;
    setIsSubmitting(true);
    try {
      const userIds = Array.from(selectedKeys);
      const { succeeded, failed } = await bulkChangeUserRoles(
        adminClient,
        userIds,
        bulkModal.newRole,
      );

      if (succeeded.length > 0) {
        addToast({
          title: t("Roles updated"),
          description: t("{{count}} user(s) changed to {{role}}", {
            count: succeeded.length,
            role: bulkModal.newRole,
          }),
          color: "success",
        });
      }

      if (failed.length > 0) {
        addToast({
          title: t("Some updates failed"),
          description: t("{{count}} user(s) could not be updated", {
            count: failed.length,
          }),
          color: "danger",
        });
      }

      setSelectedKeys(new Set());
      await onUsersChanged();
    } catch (error) {
      logger.error("Failed to bulk change user roles", error instanceof Error ? error : undefined);
      addToast({
        title: t("Error"),
        description: t("Failed to update roles. Please try again."),
        color: "danger",
      });
    } finally {
      setIsSubmitting(false);
      setBulkModal(null);
    }
  };

  const getDemotionWarning = (newRole: string): string | null => {
    if (newRole === "User") {
      return t(
        "This will remove elevated privileges. The user will only have access to assigned workflows.",
      );
    }
    if (newRole === "Manager") {
      return t("This will grant access to workflows and knowledge hub, but not admin settings.");
    }

    return null;
  };

  return (
    <div className="space-y-4 pt-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          className="flex-1"
          placeholder={t("Search users...")}
          startContent={<Icon icon="solar:magnifer-outline" width={18} />}
          value={search}
          onValueChange={setSearch}
        />
        <Select
          aria-label={t("Filter by role")}
          className="w-full sm:w-48"
          selectedKeys={[roleFilter]}
          onChange={(e) => setRoleFilter(e.target.value as "All" | Role)}
        >
          <SelectItem key="All">{t("All Roles")}</SelectItem>
          <SelectItem key="Admin">{t("Admins")}</SelectItem>
          <SelectItem key="Manager">{t("Managers")}</SelectItem>
          <SelectItem key="User">{t("Users")}</SelectItem>
        </Select>
      </div>

      {selectedCount > 0 && (
        <div className="flex items-center gap-2 p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
          <span className="text-sm text-primary">
            {t("{{count}} user(s) selected", { count: selectedCount })}
          </span>
          <Button
            color="primary"
            size="sm"
            startContent={<Icon icon="solar:shield-outline" width={16} />}
            variant="flat"
            onPress={() => setBulkModal({ newRole: "Admin" })}
          >
            {t("Make Admin")}
          </Button>
          <Button
            color="secondary"
            size="sm"
            startContent={<Icon icon="solar:shield-user-outline" width={16} />}
            variant="flat"
            onPress={() => setBulkModal({ newRole: "Manager" })}
          >
            {t("Make Manager")}
          </Button>
          <Button
            color="default"
            size="sm"
            startContent={<Icon icon="solar:user-outline" width={16} />}
            variant="flat"
            onPress={() => setBulkModal({ newRole: "User" })}
          >
            {t("Make User")}
          </Button>
          <Button size="sm" variant="light" onPress={() => setSelectedKeys(new Set())}>
            {t("Clear")}
          </Button>
        </div>
      )}

      <Table
        aria-label={t("Users table")}
        selectedKeys={selectedKeys}
        selectionMode="multiple"
        onSelectionChange={(keys) => {
          if (keys === "all") {
            setSelectedKeys(new Set(filteredUsers.map((u) => u.id)));
          } else {
            setSelectedKeys(new Set(keys as Set<string>));
          }
        }}
      >
        <TableHeader>
          <TableColumn>{t("User")}</TableColumn>
          <TableColumn>{t("Role")}</TableColumn>
          {/* TODO: Add Status column once backend tracks active/inactive state */}
          <TableColumn>{t("Workflows")}</TableColumn>
        </TableHeader>
        <TableBody emptyContent={t("No users found")} items={filteredUsers}>
          {(user) => (
            <TableRow key={user.id}>
              <TableCell>
                <UserAvatar
                  avatarProps={{ src: user.picture, size: "sm" }}
                  description={user.email}
                  name={user.name}
                />
              </TableCell>
              <TableCell>
                <Select
                  aria-label={t("Change role for {{name}}", { name: user.name })}
                  className="w-32"
                  selectedKeys={[user.role]}
                  size="sm"
                  variant="flat"
                  onChange={(e) => handleRoleChange(user, e.target.value as Role)}
                >
                  <SelectItem key="Admin">{t("Admin")}</SelectItem>
                  <SelectItem key="Manager">{t("Manager")}</SelectItem>
                  <SelectItem key="User">{t("User")}</SelectItem>
                </Select>
              </TableCell>
              <TableCell>{user.workflowCount}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Single role change confirmation */}
      <Modal isOpen={!!confirmModal} onClose={() => setConfirmModal(null)}>
        <ModalContent>
          <ModalHeader>{t("Confirm Role Change")}</ModalHeader>
          <ModalBody>
            <p>
              {t("Are you sure you want to change {{name}}'s role from {{from}} to {{to}}?", {
                name: confirmModal?.userName,
                from: confirmModal?.currentRole,
                to: confirmModal?.newRole,
              })}
            </p>
            {confirmModal?.newRole && getDemotionWarning(confirmModal.newRole) && (
              <p className="text-warning text-sm mt-2">
                {getDemotionWarning(confirmModal.newRole)}
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setConfirmModal(null)}>
              {t("Cancel")}
            </Button>
            <Button color="primary" isLoading={isSubmitting} onPress={confirmRoleChange}>
              {t("Confirm")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Bulk role change confirmation */}
      <Modal isOpen={!!bulkModal} onClose={() => setBulkModal(null)}>
        <ModalContent>
          <ModalHeader>{t("Confirm Bulk Role Change")}</ModalHeader>
          <ModalBody>
            <p>
              {t("Are you sure you want to change {{count}} user(s) to {{role}}?", {
                count: selectedCount,
                role: bulkModal?.newRole,
              })}
            </p>
            {bulkModal?.newRole && getDemotionWarning(bulkModal.newRole) && (
              <p className="text-warning text-sm mt-2">{getDemotionWarning(bulkModal.newRole)}</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setBulkModal(null)}>
              {t("Cancel")}
            </Button>
            <Button color="primary" isLoading={isSubmitting} onPress={confirmBulkRoleChange}>
              {t("Confirm")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};
